import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingBag, ShieldCheck, Truck, BadgeCheck } from "lucide-react";
import confetti from "canvas-confetti";
import { useStore } from "@/store/StoreContext";
import { imgSrc } from "@/lib/api";
import { toast } from "sonner";
import Stars from "@/components/Stars";
import CartDrawer from "@/components/CartDrawer";

const CRACKER_COLORS = ["#D4AF37", "#F6E27A", "#E67300", "#B34A00", "#FDF8F0", "#C0392B"];

function productUrlSlug(product) {
  const nameSlug = (product.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const savedSlug = product.slug || "";
  // Keep a stored suffix only when it is the 6-character uniqueness suffix
  // generated for a duplicate product name; ignore stale slugs after renaming.
  if (nameSlug && (savedSlug === nameSlug || new RegExp("^" + nameSlug + "-[a-f0-9]{6}$").test(savedSlug))) {
    return savedSlug;
  }
  return nameSlug || savedSlug || product.id;
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { products, addToCart, setCartOpen, priceOf, originalOf, money, currency } = useStore();
  const product = products.find((p) => productUrlSlug(p) === slug || (p.slug || p.id) === slug || (p.slug_history || []).includes(slug));

  useEffect(() => {
    if (product) {
      const canonicalSlug = productUrlSlug(product);
      if (canonicalSlug && canonicalSlug !== slug) navigate(`/product/${canonicalSlug}`, { replace: true });
    }
  }, [product, slug, navigate]);

  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FDF8F0] text-[#2B1E12]">
        <div className="text-center">
          <p className="font-display text-2xl">Loading product…</p>
          <Link to="/" className="mt-4 inline-block text-sm text-[#8F6E1D] underline">Back to store</Link>
        </div>
      </div>
    );
  }

  const price = priceOf(product);
  const original = originalOf(product);
  const discount = original ? Math.round((1 - price / original) * 100) : 0;

  const handleAdd = (e, goCart) => {
    confetti({ particleCount: 110, spread: 85, startVelocity: 42, origin: { x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight }, colors: CRACKER_COLORS, ticks: 180, disableForReducedMotion: true });
    const ok = addToCart(product);
    if (!ok) { toast.error("Cart limit reached — maximum 10 different products per order"); return; }
    toast.success(`${product.name} added to cart`);
    if (goCart) navigate("/checkout");
    else setCartOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#FDF8F0] text-[#2B1E12]" data-testid="product-detail-page">
      {/* mini banner */}
      <div className="relative h-36 overflow-hidden sm:h-44">
        <img src="/assets/brand/hero-banner.jpg" alt="Rudralife Rudraksha" className="h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-[#1C130B]/45" />
        <div className="absolute inset-0 mx-auto flex max-w-6xl items-center px-5 sm:px-8">
          <Link to="/" data-testid="detail-back" className="flex items-center gap-2 rounded-full bg-[#FDF8F0]/90 px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#2B1E12] transition-colors hover:bg-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </Link>
        </div>
      </div>

      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-10 sm:px-8 lg:grid-cols-2 lg:py-14">
        {/* left: images */}
        <div className="space-y-4">
          <div className="relative overflow-hidden rounded-md border border-[#E9DFC8] bg-[#F7F0DF]">
            {discount > 0 && (
              <span className="absolute left-4 top-4 z-10 rounded-full bg-[#B34A00] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white">{discount}% Off</span>
            )}
            <img data-testid="detail-image" src={imgSrc(product.image)} alt={product.name} className="aspect-square w-full object-contain p-8" />
          </div>
        </div>

        {/* right: content */}
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#B34A00]">Rudralife · Diwali 2026</p>
          <h1 data-testid="detail-name" className="font-display mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">{product.name}</h1>
          <div className="mt-3"><Stars slug={product.slug || product.id} size={16} /></div>

          <div className="mt-5 flex items-baseline gap-3">
            <span data-testid="detail-price" className="font-display text-4xl font-bold text-[#8F6E1D]">{money(price)}</span>
            {original && <span data-testid="detail-original" className="text-lg font-medium text-[#6B5B45]/75 line-through">{money(original)}</span>}
          </div>
          <p className="mt-1.5 text-xs font-medium text-[#6B5B45]">
            {currency === "USD" ? "$40 international delivery · ships worldwide" : "FREE shipping across India"}
          </p>

          <p data-testid="detail-description" className="mt-5 max-w-lg text-base leading-relaxed text-[#6B5B45]">
            {product.description}
          </p>

          <ul className="mt-6 space-y-2.5 text-sm text-[#6B5B45]">
            <li className="flex items-center gap-2.5"><BadgeCheck className="h-4 w-4 text-[#B08D2B]" /> Lab-certified authentic Rudraksha</li>
            <li className="flex items-center gap-2.5"><ShieldCheck className="h-4 w-4 text-[#B08D2B]" /> Secure payment via Razorpay</li>
            <li className="flex items-center gap-2.5"><Truck className="h-4 w-4 text-[#B08D2B]" /> Vedic-energised and safely shipped</li>
          </ul>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              data-testid="detail-add-to-cart"
              onClick={(e) => handleAdd(e, false)}
              className="flex items-center gap-2 bg-[#D4AF37] px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] text-[#1C130B] transition-colors duration-300 hover:bg-[#8F6E1D] hover:text-white"
            >
              <ShoppingBag className="h-4 w-4" /> Add to Cart
            </button>
            <button
              data-testid="detail-buy-now"
              onClick={(e) => handleAdd(e, true)}
              className="border border-[#8F6E1D]/50 px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] text-[#8F6E1D] transition-colors duration-300 hover:bg-[#D4AF37] hover:text-[#1C130B]"
            >
              Buy Now
            </button>
          </div>
        </div>
      </div>
      <CartDrawer />
    </div>
  );
}
